import { useEffect, useState } from 'react'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'

/**
 * Team / seats view for the tenant.
 *
 * Reads real data from /me/team + /me/subscription. For invite / role
 * changes we use Clerk's OrganizationProfile — Clerk owns organization
 * membership, we just display the current state here.
 */
export default function V2TeamSettings() {
  const api = useV2Api()
  const [team, setTeam] = useState([])
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([api.getTeam(), api.getSubscription()])
      .then(([t, s]) => {
        if (cancelled) return
        setTeam(t?.data || [])
        setSubscription(s?.data || null)
      })
      .catch((err) => !cancelled && setError(err))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [api])

  if (loading) return <div style={{ padding: '1rem' }}>Loading team…</div>
  if (error) {
    return (
      <div style={{
        padding: '10px 14px', borderRadius: 6, background: 'rgba(139,58,58,0.08)',
        color: '#8B3A3A', border: '1px solid #8B3A3A'
      }}>{friendlyErrorMessage(error, 'Failed to load team.')}</div>
    )
  }

  const activeSeats = team.filter(u => u.status === 'ACTIVE').length
  const seatLimit = subscription?.seat_limit || 0
  const seatsRemaining = Math.max(0, seatLimit - activeSeats)
  const atCapacity = seatsRemaining === 0

  return (
    <div>
      <div style={{
        padding: 16, borderRadius: 8, background: '#F3F2ED',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 18
      }}>
        <div>
          <div style={{ fontSize: 13, color: '#8F8F85' }}>Seat usage</div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>
            {activeSeats} / {seatLimit} active
          </div>
          <div style={{ fontSize: 12, color: atCapacity ? '#8B3A3A' : '#8F8F85', marginTop: 2 }}>
            {atCapacity
              ? 'At capacity. Contact sales to add more seats.'
              : `${seatsRemaining} seat${seatsRemaining === 1 ? '' : 's'} available`}
          </div>
        </div>
        <a
          href="mailto:sales@marteu.com?subject=Marteu%20freight%20%E2%80%94%20more%20seats"
          className="v2-btn v2-btn--outline"
        >
          Contact sales
        </a>
      </div>

      <div style={{ border: '1px solid #E4E2DA', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#FBFAF6', textAlign: 'left' }}>
              <th style={{ padding: 12, fontSize: 12, color: '#8F8F85', fontWeight: 600 }}>Email</th>
              <th style={{ padding: 12, fontSize: 12, color: '#8F8F85', fontWeight: 600 }}>Name</th>
              <th style={{ padding: 12, fontSize: 12, color: '#8F8F85', fontWeight: 600 }}>Role</th>
              <th style={{ padding: 12, fontSize: 12, color: '#8F8F85', fontWeight: 600 }}>Status</th>
              <th style={{ padding: 12, fontSize: 12, color: '#8F8F85', fontWeight: 600 }}>Last login</th>
            </tr>
          </thead>
          <tbody>
            {team.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#8F8F85' }}>
                No team members yet. Invite someone via the organization menu (top-right).
              </td></tr>
            ) : (
              team.map(u => (
                <tr key={u.id} style={{ borderTop: '1px solid #E4E2DA' }}>
                  <td style={{ padding: 12 }}>{u.email}</td>
                  <td style={{ padding: 12 }}>{u.name || ''}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: u.role === 'OWNER' ? 'rgba(43,37,96,0.1)'
                        : u.role === 'ADMIN' ? 'rgba(143,67,47,0.12)'
                        : 'rgba(162,162,153,0.2)',
                      color: u.role === 'OWNER' ? '#2B2560'
                        : u.role === 'ADMIN' ? '#8F432F'
                        : '#666'
                    }}>{u.role}</span>
                  </td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: u.status === 'ACTIVE' ? 'rgba(92,122,84,0.15)' : 'rgba(139,58,58,0.12)',
                      color: u.status === 'ACTIVE' ? '#5C7A54' : '#8B3A3A'
                    }}>{u.status}</span>
                  </td>
                  <td style={{ padding: 12, color: '#8F8F85', fontSize: 13 }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 12, color: '#8F8F85', marginTop: 12 }}>
        Invitations and role changes are managed through the organization menu in the top-right
        (Clerk handles email invites, acceptance, and role enforcement).
      </p>
    </div>
  )
}
